import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard, Calendar, Users, CreditCard, Settings, ChevronLeft, ChevronRight,
  ScanLine, Bot, MessageSquareText, BarChart3, Link2, CalendarClock
} from 'lucide-react';
import { Button } from '../ui/button';

const WaffleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="2.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="2.5" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="7" cy="7" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="7" r="1.75" fill="#888888" />
    <circle cx="2.5" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="7" cy="11.5" r="1.75" fill="#888888" />
    <circle cx="11.5" cy="11.5" r="1.75" fill="#888888" />
  </svg>
);

/**
 * Sidebar visibility contract (2026-04-21):
 *   Schedule is shown in ALL three app sidebars (Lumina, Echo, RevQ) in
 *   BOTH demo and real modes. Mode-correctness of the rendered content is
 *   handled downstream by `SchedulerRoute` which dispatches demo →
 *   `LegacyEchoScheduler` and real → API-backed `EchoScheduler`. Never
 *   gate the menu entry itself on `isDemo` — that hides a product surface
 *   from real users and breaks IA consistency across apps.
 */
const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/echo', icon: LayoutDashboard },
      { name: 'Schedule', href: '/echo/scheduler', icon: CalendarClock },
    ],
  },
  {
    label: 'Operations',
    items: [
      { name: 'Patients', href: '/echo/patients', icon: Users },
      { name: 'Comms Hub', href: '/echo/comms', icon: MessageSquareText },
      { name: 'Lens', href: '/echo/lens', icon: ScanLine },
      { name: 'AI Agents', href: '/echo/agents', icon: Bot },
    ],
  },
  {
    label: 'Insights',
    items: [
      { name: 'Analytics', href: '/echo/analytics', icon: BarChart3 },
      { name: 'Billing', href: '/echo/billing', icon: CreditCard },
      { name: 'Integrations', href: '/echo/integrations', icon: Link2 },
    ],
  },
];

const EchoSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();

  return (
    <aside className={cn(
      "fixed left-0 top-14 h-[calc(100vh-3.5rem)] bg-white border-r border-slate-200 transition-all duration-300 z-40 flex flex-col",
      collapsed ? "w-16" : "w-60"
    )} data-testid="sidebar">

      {/* Waffle — All Apps */}
      <div className="px-3 pt-3 pb-1">
        <Link to="/landing"
          className="flex items-center gap-2.5 px-3 py-2.5 border border-slate-200 transition-all"
          style={{ borderRadius: 6 }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(107,92,231,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          data-testid="sidebar-all-apps"
        >
          <WaffleIcon />
          {!collapsed && (
            <div>
              <p style={{ fontSize: 12, fontWeight: 500, color: '#333333', lineHeight: '14px', margin: 0 }}>All apps</p>
              <p style={{ fontSize: 11, fontWeight: 400, color: '#888888', lineHeight: '13px', margin: 0 }}>Aria One platform</p>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 py-3 px-3 space-y-5 overflow-y-auto">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label}>
            {!collapsed && <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">{section.label}</p>}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                // Cross-app active check: when the user is on the canonical
                // `/lumina/scheduler` path (or its `/echo/scheduler` redirect
                // alias), Schedule entries in any app sidebar must appear
                // active — not just the sidebar of the hosting app.
                const SCHEDULER_PATHS = ['/lumina/scheduler', '/echo/scheduler', '/revq/scheduler'];
                const isSchedulerItem = SCHEDULER_PATHS.includes(item.href);
                const isOnScheduler = SCHEDULER_PATHS.includes(location.pathname);
                const isActive =
                  (isSchedulerItem && isOnScheduler) ||
                  location.pathname === item.href ||
                  (item.href !== '/echo' && location.pathname.startsWith(item.href));
                return (
                  <Link key={item.name} to={item.href} data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                    className={cn("flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all",
                      isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800",
                      collapsed && "justify-center px-2"
                    )}>
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {!collapsed && <span>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-slate-200 p-3">
        <Link to="/echo/settings" data-testid="nav-settings"
          className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-all text-slate-500 hover:bg-slate-50 hover:text-slate-800", collapsed && "justify-center px-2")}>
          <Settings className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
      </div>

      <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-8 h-6 w-6 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 shadow-sm" data-testid="sidebar-toggle">
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>
    </aside>
  );
};

export default EchoSidebar;
